// Dashboard del módulo Control de Ganado. Ganadería → Resumen → Dashboard.
import { useQuery } from '@tanstack/react-query';
import { LayoutDashboard, Loader2, MapPinned, PawPrint, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useTabsStore } from '@/stores/tabs.store';
import { DashboardData, estadoInfo, ganRpc } from './shared';

function useIr() {
  const openTab = useTabsStore((s) => s.openTab);
  return (path: string, title: string) => openTab({ id: path, title, path });
}

function BarraLista({ titulo, filas, colorCls }: { titulo: string; filas: { etiqueta: string; n: number }[]; colorCls: string }) {
  const max = Math.max(1, ...filas.map((f) => f.n));
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">{titulo}</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {filas.map((f) => (
          <div key={f.etiqueta} className="flex items-center gap-2 text-sm">
            <span className="w-32 shrink-0 truncate text-xs text-muted-foreground" title={f.etiqueta}>{f.etiqueta}</span>
            <div className="flex-1 h-3 rounded bg-muted overflow-hidden">
              <div className={`h-full ${colorCls}`} style={{ width: `${(f.n / max) * 100}%` }} />
            </div>
            <span className="w-10 shrink-0 text-right font-mono tabular-nums text-xs">{f.n}</span>
          </div>
        ))}
        {!filas.length && <p className="text-sm text-muted-foreground">Sin datos</p>}
      </CardContent>
    </Card>
  );
}

export function DashboardGanaderia() {
  const ir = useIr();
  const { data, isFetching } = useQuery<DashboardData>({
    queryKey: ['gan-dashboard'],
    queryFn: () => ganRpc<DashboardData>('gan_dashboard'),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <LayoutDashboard className="w-5 h-5" /> Dashboard de Ganadería
        </h1>
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </div>

      {!data ? (
        <div className="p-6 flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" /> Cargando dashboard…
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Card className="cursor-pointer hover:bg-muted/40" onClick={() => ir('/ganaderia/hato', 'Hato')}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><PawPrint className="w-4 h-4" /> Total activos</div>
                <div className="text-3xl font-semibold font-mono tabular-nums mt-1">{data.total_activos}</div>
                <div className="text-xs text-muted-foreground mt-0.5">animales en el hato · ver Hato →</div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:bg-muted/40" onClick={() => ir('/ganaderia/predios', 'Predios y potreros')}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><MapPinned className="w-4 h-4" /> Lotes con animales</div>
                <div className="text-3xl font-semibold font-mono tabular-nums mt-1">{data.por_lote.length}</div>
                <div className="text-xs text-muted-foreground mt-0.5">grupos de manejo con hato asignado</div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:bg-muted/40" onClick={() => ir('/ganaderia/lotes', 'Lotes')}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><Users className="w-4 h-4" /> Categorías registradas</div>
                <div className="text-3xl font-semibold font-mono tabular-nums mt-1">{data.por_categoria.length}</div>
                <div className="text-xs text-muted-foreground mt-0.5">clasificaciones de edad/uso en el hato</div>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <BarraLista
              titulo="Por categoría"
              colorCls="bg-emerald-500"
              filas={data.por_categoria.map((c) => ({ etiqueta: c.categoria || 'Sin categoría', n: c.n }))}
            />
            <BarraLista
              titulo="Por lote"
              colorCls="bg-sky-500"
              filas={data.por_lote.map((l) => ({ etiqueta: l.lote || 'Sin lote', n: l.n }))}
            />
          </div>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Por estado</CardTitle></CardHeader>
            <CardContent>
              <table className="w-full text-sm max-w-md">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Estado</th><th className="py-2 pr-3 text-right">Animales</th>
                </tr></thead>
                <tbody>
                  {data.por_estado.map((e, i) => {
                    const info = estadoInfo(e.estado);
                    return (
                      <tr key={e.estado} className="border-b last:border-0">
                        <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                        <td className="py-2 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${info.cls}`}>{info.label}</span></td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums">{e.n}</td>
                      </tr>
                    );
                  })}
                  {data.por_estado.length > 0 && (
                    <tr className="border-t-2 font-semibold bg-muted/30">
                      <td className="py-2 pr-3" colSpan={2}>Totales ({data.por_estado.length})</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{data.por_estado.reduce((a, e) => a + Number(e.n), 0)}</td>
                    </tr>
                  )}
                  {!data.por_estado.length && <tr><td colSpan={3} className="py-6 text-center text-muted-foreground">Sin datos</td></tr>}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
